import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import MailLabelPicker from "./MailLabelPicker";
import { mailService } from "../../services/api";

const folders = { inbox: "Inbox", unread: "Unread", starred: "Starred", sent: "Sent", all: "All mail" };
const errorText = error => typeof error.response?.data?.detail === "string" ? error.response.data.detail : "Gmail could not be loaded. Check the connection and try again.";
const stamp = (value, zone) => new Date(value).toLocaleString(undefined, { timeZone: zone || "UTC", dateStyle: "medium", timeStyle: "short" });

export default function MailboxBrowser() {
  const navigate = useNavigate(), cache = useQueryClient();
  const [folder, setFolder] = useState("inbox"), [query, setQuery] = useState("");
  const [labelToken, setLabelToken] = useState("");
  const pageSpec = page => ({folder: page.folder, query: page.query, ...(page.label_token ? {label_token: page.label_token} : {})});
  const [page, setPage] = useState(null), [cursor, setCursor] = useState(null), [history, setHistory] = useState([]);
  const [busy, setBusy] = useState(false), [error, setError] = useState("");
  async function load(spec, next = null, prior = []) {
    if (busy) return;
    setBusy(true); setError("");
    try {
      const result = await mailService.browse({ ...spec, cursor: next });
      setPage(result); setCursor(next); setHistory(prior);
    } catch (failure) { setError(errorText(failure)); }
    finally { setBusy(false); }
  }
  async function open(item) {
    if (busy) return;
    setBusy(true); setError("");
    try {
      const thread = await mailService.openMailbox({ selection_token: item.selection_token });
      await cache.invalidateQueries({ queryKey: ["mail"] });
      navigate(`/mail/threads/${thread.id}`);
    } catch (failure) { setError(errorText(failure)); }
    finally { setBusy(false); }
  }
  return <section className="surface-panel space-y-4 p-5" aria-label="Connected Gmail mailbox">
    <div>
      <h2 className="font-bold text-lg">Browse Gmail</h2>
      <p className="text-sm text-[#66716a]">Find current conversations in your connected mailbox. Opening one saves its text for replies and follow-ups; Gmail unread status stays unchanged.</p>
    </div>
    <form className="space-y-3" onSubmit={e => { e.preventDefault(); load({ folder, query: query.trim(), ...(labelToken ? {label_token: labelToken} : {}) }); }}>
      <div className="grid gap-3 sm:grid-cols-[150px_1fr]">
        <label className="text-sm font-semibold">Mailbox view
          <select className="field-input mt-1" value={folder} onChange={e => setFolder(e.target.value)}>{Object.entries(folders).map(([id, name]) => <option key={id} value={id}>{name}</option>)}</select>
        </label>
        <label className="text-sm font-semibold">Search Gmail (optional)
          <input className="field-input mt-1" maxLength={500} value={query} onChange={e => setQuery(e.target.value)} placeholder="Subject, sender, or Gmail search such as from:someone" />
        </label>
      </div>
      <MailLabelPicker value={labelToken} onChange={setLabelToken} disabled={busy} optional />
      <div className="flex flex-wrap items-center gap-3">
        <button className="primary-button" disabled={busy}>{busy ? "Loading…" : "Load conversations"}</button>
        <Link className="text-sm underline" to="/integrations">Manage Google connection</Link>
      </div>
      <p className="text-xs text-[#66716a]">Ten conversations per page. Spam and Trash are excluded. A search uses Gmail’s syntax within the selected view.</p>
    </form>
    {error && <p role="alert" className="text-sm text-red-800">{error}</p>}
    {!page && !busy && <p className="text-sm">Choose a view and load conversations to start.</p>}
    {page && <div className="space-y-3">
      <div>
        <p className="font-semibold text-sm">{folders[page.folder]}{page.label ? ` · Label: ${page.label.name}` : ""} · {page.mailbox}</p>
        {page.query && <p className="text-xs">Search: {page.query}</p>}
        <p className="text-xs text-[#66716a]">Page {history.length + 1} · Checked {stamp(page.checked_at, page.timezone)}{page.result_size_estimate != null && ` · Gmail estimates ${page.result_size_estimate} matches`}</p>
      </div>
      {!page.items.length && <p>No conversations matched this view and search.</p>}
      {page.items.map(item => <article className="rounded-lg border border-[#dce3dd] p-4 space-y-2" key={item.remote_id}>
        {item.available ? <>
          <div className="flex flex-wrap gap-2 items-center"><h3 className="font-bold">{item.subject}</h3>{item.unread && <span className="status-pill">Unread</span>}{item.starred && <span className="status-pill">Starred</span>}</div>
          <p className="text-xs">{item.from || "Unknown sender"} · {stamp(item.latest_at, page.timezone)} · {item.message_count} messages</p>
          <p className="text-sm whitespace-pre-wrap">{item.snippet}</p>
          <button className="secondary-button text-sm" disabled={busy} onClick={() => open(item)}>Open {item.subject}</button>
        </> : <p className="text-sm">{item.error}</p>}
      </article>)}
      <div className="flex flex-wrap gap-3">
        {history.length > 0 && <button className="secondary-button" disabled={busy} onClick={() => load(pageSpec(page), history[history.length - 1], history.slice(0, -1))}>Previous page</button>}
        {page.next_cursor && <button className="secondary-button" disabled={busy} onClick={() => load(pageSpec(page), page.next_cursor, [...history,cursor])}>Next page</button>}
        <button className="secondary-button" disabled={busy} onClick={() => load(pageSpec(page))}>Refresh from first page</button>
      </div>
      <p className="text-xs text-[#66716a]">Mailbox contents may change between pages. Refresh from the first page if a page or selection expires. Message snippets are previews; open the conversation before acting on it.</p>
    </div>}
  </section>;
}
